import { useEffect, useRef, useState } from "react";

/**
 * Drop-in replacement for a `<select>` whose dropdown popup must be
 * able to grow wider than the closed trigger. Native `<select>` on
 * iPadOS Safari locks the popup to the trigger's width, which wraps
 * longer option labels onto two lines. This component renders the
 * popup as an absolutely positioned `<ul>` with `width: max-content`
 * so each option lays out on a single line.
 *
 * Same accessibility footprint as Select: keyboard-navigable, closes
 * on outside click, focus returns to the trigger on close.
 */
export function WideSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  required,
}: {
  /** Optional — when omitted, the component renders just the control
   *  (used by per-row layouts that supply their own label column). */
  label?: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (open) {
      const i = options.findIndex((o) => o.value === value);
      setHighlight(i >= 0 ? i : 0);
    }
  }, [open, options, value]);

  function pick(v: string) {
    onChange(v);
    setOpen(false);
    buttonRef.current?.focus();
  }

  function onKey(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && open) {
      e.preventDefault();
      const opt = options[highlight];
      if (opt) pick(opt.value);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef}>
      {label ? (
        <label className="block text-sm font-medium text-slate-700">
          {label}
          {required ? <span className="text-rose-600"> *</span> : null}
        </label>
      ) : null}
      <div className={label ? "relative mt-1" : "relative"}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          onKeyDown={onKey}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
        >
          <span className={selected ? "text-slate-900" : "text-slate-400"}>
            {selected ? selected.label : placeholder ?? "— elegir —"}
          </span>
          <span aria-hidden className="ml-2 text-xs text-slate-400">▾</span>
        </button>
        {open ? (
          <ul
            role="listbox"
            className="absolute left-0 top-full z-20 mt-1 max-h-72 min-w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg"
            style={{ width: "max-content", maxWidth: "min(40rem, 90vw)" }}
          >
            {options.map((o, i) => (
              <li
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(o.value);
                }}
                onMouseEnter={() => setHighlight(i)}
                className={
                  "cursor-pointer whitespace-nowrap px-3 py-1.5 text-sm " +
                  (highlight === i
                    ? "bg-sky-100 text-slate-900"
                    : o.value === value
                      ? "bg-sky-50 text-slate-900"
                      : "text-slate-800 hover:bg-sky-50")
                }
              >
                {o.label}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
